import React, { useContext, useMemo } from "react";
import { AppContext } from "../../context/EditSessionsContext";
import { blocksFormat } from "../../utils/sessionFormat";
import { selectedDayEn } from "@utils/days";
import CardSessionNew from "../card-session";
import { useProgrammingEditStore } from "@context/useProgrammingEditStore";
import css from "./compare.module.scss";

const Compare: React.FC = () => {
  const { state } = useContext(AppContext);
  const session = state?.result?.selectSession;
  const sessionCreate = state?.result?.selectCreateSession;
  const sessionToDeleteSelected = state?.result?.sessionToDeleteSelected;

  // Programming mode
  const isProgrammingMode = useProgrammingEditStore(s => s.isProgrammingResultsActive);
  const previewSections = useProgrammingEditStore(s => s.previewSections);

  const formatDay = (day: string, start: string, end: string, days: string[] = []) => {
    const dayFormat = (days.length ? days : [day])
      .map(value => selectedDayEn(value).label)
      .filter(Boolean)
      .join(", ");
    const startTime = start?.substring(0, 5) || "";
    const endTime = end?.substring(0, 5) || "";
    return `${dayFormat} ${startTime} - ${endTime}`;
  };

  const linkWeekValuesById = useMemo(() => {
    return (state?.link?.weeks ?? []).reduce((acc, week) => {
      acc[week?.id] = week?.value?.toString();
      return acc;
    }, {});
  }, [state]);

  // --- Programming mode: build sessionToCreate from preview session ---
  const programmingSessionToCreate = useMemo(() => {
    if (!isProgrammingMode || !sessionCreate) return null;

    // Find the matching session in previewSections
    let previewSession: any = null;
    for (const section of previewSections ?? []) {
      const found = section.sessions.find(s => s.id === (sessionCreate as any)?.id);
      if (found) {
        previewSession = found;
        break;
      }
    }

    if (!previewSession) return null;

    const assignment = previewSession.assignment;
    const blockRange = assignment?.blockRange;
    const dayFormat = blockRange
      ? formatDay(
          assignment.day ?? "",
          blockRange.start?.startingTime ?? "",
          blockRange.end?.endingTime ?? "",
        )
      : "No requiere horario";

    const classrooms = (previewSession.classrooms ?? []).map((c: any) => ({
      code: c.code,
      name: c.name,
      building: c.building?.code,
    }));

    const intervals = (assignment?.intervals ?? []).map(
      (i: any) => linkWeekValuesById[i.id] ?? i.name ?? i.id,
    );

    const adaptedData = {
      blockRange: dayFormat,
      instructors: previewSession.instructors ?? [],
      classrooms,
      intervals,
      vacancies: state?.link?.info?.vacancies?.toString() ?? "-",
    };

    return {
      session: sessionCreate,
      adaptedData,
    };
  }, [
    isProgrammingMode,
    sessionCreate,
    previewSections,
    linkWeekValuesById,
    state?.link?.info?.vacancies,
  ]);

  // --- Programming mode: build sessionToDelete from original link section ---
  const programmingSessionToDelete = useMemo(() => {
    if (!isProgrammingMode || !sessionToDeleteSelected) return null;

    const session = sessionToDeleteSelected as any;
    const blockRange = blocksFormat(session);
    const dayFormat = blockRange
      ? formatDay(blockRange.day ?? "", blockRange.startTime ?? "", blockRange.endTime ?? "")
      : "No requiere horario";

    const classrooms = (session?.classrooms ?? []).map((c: any) => ({
      code: c.code,
      name: c.name,
      building: c.building?.code,
    }));

    const intervals = (session?.assignment?.intervals ?? []).map(
      (i: any) => linkWeekValuesById[i.id] ?? i.name ?? i.id,
    );

    return {
      session: sessionToDeleteSelected,
      adaptedData: {
        blockRange: blockRange?.selected === "hours" ? dayFormat : "No requiere horario",
        instructors: (session?.instructors ?? []).map((inst: any) => ({
          code: inst.code,
          name: inst.name,
        })),
        classrooms,
        intervals,
        vacancies: state?.link?.info?.vacancies?.toString() ?? "-",
      },
    };
  }, [
    isProgrammingMode,
    sessionToDeleteSelected,
    linkWeekValuesById,
    state?.link?.info?.vacancies,
  ]);

  // --- Assignment mode ---
  const sessionIntervals = useMemo(() => {
    if (isProgrammingMode) return { originalSessionIntervals: [], modifiedSessionIntervals: [] };

    const saved = state?.form?.savedSessions?.find(save => save?.id === session?.id);

    const originalSessionIntervals = (session?.assignment?.intervals ?? []).map(interval =>
      state?.link?.weeks ? linkWeekValuesById[interval?.id] : interval?.id,
    );

    const modifiedSessionIntervals = (saved?.intervals ?? [])
      .filter(interval => interval.checked)
      .map(interval => (state?.link?.weeks ? linkWeekValuesById[interval?.id] : interval?.id));

    return { originalSessionIntervals, modifiedSessionIntervals };
  }, [state, isProgrammingMode]);

  const sessionToCreate = useMemo(() => {
    if (isProgrammingMode) return null;
    if (!sessionCreate && !sessionToDeleteSelected) return null;

    const sess = sessionCreate ?? sessionToDeleteSelected;

    const dayFormat = formatDay(
      sess.blocks?.day,
      sess.blocks?.startTime,
      sess.blocks?.endTime,
      sess.blocks?.days,
    );

    const classrooms = sess?.classrooms?.map(clas => ({
      code: clas.code,
      name: clas.name,
      building: clas.building?.code,
    }));

    const linkWeeksById = (state?.link?.weeks ?? []).reduce((acc, week) => {
      acc[week.id] = week;
      return acc;
    }, {});
    const newSessionWeeksIds = (sess?.intervals ?? [])
      .filter(interval => interval.checked)
      .map(interval => (interval.id in linkWeeksById ? linkWeeksById[interval.id] : interval.id))
      .map(interval => interval?.value?.toString() || "");

    const sessionToCreate = {
      blockRange: sess?.blocks?.selected === "hours" ? dayFormat : "No requiere horario",
      instructors: sess?.instructors ?? [],
      classrooms: classrooms as any[],
      intervals: newSessionWeeksIds,
      vacancies: sess?.section?.vacancies?.toString() || "-",
    };

    return { session: sess, adaptedData: sessionToCreate };
  }, [state, sessionCreate, sessionToDeleteSelected, isProgrammingMode]);

  const originSession = useMemo(() => {
    if (isProgrammingMode) return null;

    const selectedOgSection = state?.link?.sections?.find(
      section => section.id === session?.section?.id,
    );
    const selectedOgSession = [
      ...(selectedOgSection?.sessions ?? []),
      ...(selectedOgSection?.unasignedSessions ?? []),
    ].find(ogSession => ogSession.id === session?.id);

    const blockRange = blocksFormat(selectedOgSession);
    const dayFormat = formatDay(
      blockRange?.day,
      blockRange?.startTime,
      blockRange?.endTime,
      blockRange?.days,
    );

    return {
      session: selectedOgSession,
      adaptedData: {
        blockRange: blockRange?.selected === "hours" ? dayFormat : "No requiere horario",
        instructors: selectedOgSession?.instructors.map(instructor => ({
          code: instructor.code,
          name: instructor.name,
        })),
        classrooms: selectedOgSession?.classrooms.map(clas => ({
          code: clas.code,
          name: clas.name,
          building: clas.building?.code,
        })),
        vacancies: selectedOgSession?.section?.vacancies?.toString() || "-",
        intervals: sessionIntervals?.originalSessionIntervals,
      } as any,
    };
  }, [session, state, isProgrammingMode]);

  const editedSession = useMemo(() => {
    if (isProgrammingMode) return null;
    if (!session || !state?.form?.savedSessions || sessionCreate || sessionToDeleteSelected) {
      return null;
    }

    const saved = state?.form?.savedSessions?.find(save => save.id === session.id);
    if (!saved) return null;

    const dayFormat = formatDay(
      saved?.blocks?.day,
      saved?.blocks?.startTime,
      saved?.blocks?.endTime,
      saved?.blocks?.days,
    );
    const classrooms = (saved?.classrooms ?? [])?.map(clas => ({
      code: clas.code,
      name: clas.name,
      building: clas.building?.code,
    }));

    return {
      blockRange: saved.blocks?.selected === "hours" ? dayFormat : "No requiere horario",
      instructors: (saved?.instructors ?? [])?.map(instructor => ({
        code: instructor.code,
        name: instructor.name,
      })),
      classrooms: classrooms as any,
      vacancies: session?.section?.vacancies?.toString() || "-",
      intervals: sessionIntervals.modifiedSessionIntervals,
    };
  }, [session, state, isProgrammingMode]);

  if (!session && !sessionCreate && !sessionToDeleteSelected) return null;

  // --- Programming mode render ---
  if (isProgrammingMode && sessionCreate && programmingSessionToCreate) {
    return (
      <section className={css.compare}>
        <CardSessionNew
          originData={programmingSessionToCreate.adaptedData}
          session={programmingSessionToCreate.session}
          isNewSession
        />
      </section>
    );
  }

  if (isProgrammingMode && sessionToDeleteSelected && programmingSessionToDelete) {
    return (
      <section className={css.compare}>
        <CardSessionNew
          originData={programmingSessionToDelete.adaptedData}
          session={programmingSessionToDelete.session}
          isDeletedSession
        />
      </section>
    );
  }

  // --- Assignment mode render ---
  return (
    <section className={css.compare}>
      {session && (
        <CardSessionNew
          originData={originSession?.adaptedData}
          editedData={editedSession}
          session={session}
        />
      )}
      {sessionCreate && (
        <CardSessionNew
          originData={sessionToCreate?.adaptedData}
          session={sessionToCreate?.session}
          isNewSession
        />
      )}
      {sessionToDeleteSelected && (
        <CardSessionNew
          originData={sessionToCreate?.adaptedData}
          session={sessionToCreate?.session}
          isDeletedSession
        />
      )}
    </section>
  );
};

export default Compare;
